import { FichaService } from './../../ficha-veiculo/ficha-veiculo.service';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  AsyncValidatorFn,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { take, takeWhile } from 'rxjs/operators';
import { FichaCadastro } from '../../ficha-veiculo/ficha-cadastro/ficha-cadastro';
import { onlyDigits } from '../helper/utils';
import { celularValidator } from '../../directives/celular-validator.directive';

@Component({
  selector: 'app-dados-referencia',
  templateUrl: './dados-referencia.component.html',
  styleUrls: ['./dados-referencia.component.scss'],
})
export class DadosReferenciaComponent implements OnInit, OnDestroy {
  @Input() fichaCadastro: FichaCadastro;
  @Input() referencia;
  @Input() indexReferencia?: boolean;

  @Output() dadosReferenciaFormValidEmit = new EventEmitter();
  @Output() dadosReferenciaFormEmit = new EventEmitter();

  dadosReferenciaForm: FormGroup;
  showRelacaoClienteOutro = false;
  relacaoClienteOutroTouched = false;
  isAlive = true;
  public errorMessage: string;

  constructor(
    private readonly fb: FormBuilder,
    private readonly fichaService: FichaService
  ) {}

  ngOnInit() {
    this.initForm();
    this.initObservables();
  }

  ngOnDestroy() {
    this.isAlive = false;
  }

  initForm() {
    this.dadosReferenciaForm = this.fb.group({
      nome: ['', [Validators.required, Validators.maxLength(60)]],
      foneCelular: this.fb.control('', {
        validators: [celularValidator],
        asyncValidators: [this.validCelphone()],
        updateOn: 'blur',
      }),
      foneResidencial: [undefined],
      foneOutro: [undefined],
      idGrauRelacionamento: ['', [Validators.required]],
      relacaoClienteOutro: ['', [Validators.required]],
    });
  }

  initObservables() {
    this.dadosReferenciaForm.statusChanges.subscribe(() => {
      this.outputValues();
    });
    this.dadosReferenciaForm.valueChanges
      .pipe(takeWhile(() => this.isAlive))
      .subscribe(() => {
        this.outputValues();
      });
  }

  private outputValues() {
    this.dadosReferenciaFormValidEmit.next(this.isValid);
    this.dadosReferenciaFormEmit.next(this.dadosReferenciaForm.getRawValue());
  }

  onChangeRelacaoCliente() {
    this.relacaoClienteOutro.reset();

    this.showRelacaoClienteOutro = this.idGrauRelacionamento.value === '4';
  }

  onBlurRelacaoClienteOutro() {
    if (!this.relacaoClienteOutro.value && this.showRelacaoClienteOutro) {
      this.relacaoClienteOutroTouched = true;
    }
  }

  setDadosRecuperados(clienteReferencia) {
    if (clienteReferencia) {
      this.dadosReferenciaForm.patchValue({
        nome: clienteReferencia.nome,
        foneCelular: clienteReferencia.telefone
          ? clienteReferencia.telefone.ddd + clienteReferencia.telefone.numero
          : undefined,
        foneResidencial: clienteReferencia.telefone
          ? clienteReferencia.telefone.ddd + clienteReferencia.telefone.numero
          : undefined,
        foneOutro: clienteReferencia.telefone
          ? clienteReferencia.telefone.ddd + clienteReferencia.telefone.numero
          : undefined,
        idGrauRelacionamento: clienteReferencia.idGrauRelacionamento,
        relacaoClienteOutro: clienteReferencia.relacaoClienteOutro
          ? clienteReferencia.relacaoClienteOutro
          : undefined,
      });
    }
  }

  getIndexReferencia(): string {
    return this.indexReferencia ? `${this.indexReferencia}ª` : '';
  }

  get nome(): AbstractControl {
    return this.dadosReferenciaForm.get('nome');
  }

  get idGrauRelacionamento(): AbstractControl {
    return this.dadosReferenciaForm.get('idGrauRelacionamento');
  }

  get relacaoClienteOutro(): AbstractControl {
    return this.dadosReferenciaForm.get('relacaoClienteOutro');
  }

  get foneCelular(): AbstractControl {
    return this.dadosReferenciaForm.get('foneCelular');
  }

  get foneResidencial(): AbstractControl {
    return this.dadosReferenciaForm.get('foneResidencial');
  }

  get foneOutro(): AbstractControl {
    return this.dadosReferenciaForm.get('foneOutro');
  }

  get isValid() {
    if (this.idGrauRelacionamento.value === '4') {
      return (
        this.nome.valid &&
        this.idGrauRelacionamento.valid &&
        this.relacaoClienteOutro.value !== '' &&
        this.relacaoClienteOutro.value !== null &&
        this.fonesValid
      );
    }

    return (
      this.nome.valid && this.idGrauRelacionamento.valid && this.fonesValid
    );
  }

  get fonesValid() {
    return (
      this.foneCelular.value !== '' &&
      this.foneCelular.value !== null &&
      this.foneCelular.valid
    );
  }

  getDddAndCelular(value): { ddd: string; telefone: string } {
    if (!value || value === '') return { ddd: '', telefone: '' };
    const digits = onlyDigits(value);
    const ddd = digits.substring(0, 2);
    const telefone = digits.substring(2);
    return { ddd, telefone };
  }

  validCelphone(): AsyncValidatorFn {
    this.errorMessage = null;
    return (control: AbstractControl): Promise<ValidationErrors | null> => {
      if (!control.value || control.value.length === 0) {
        return Promise.resolve(null);
      }
      const dddCelular = this.getDddAndCelular(control.value);
      return this.fichaService
        .verifyCellPhone(dddCelular.ddd, dddCelular.telefone)
        .pipe(take(1))
        .toPromise()
        .then((res) => {
          if (res.status !== 'FAIL') {
            this.errorMessage = null;
            return null;
          }
          this.errorMessage = res.messages[0];
          return { genericErrorMessage: res.messages[0] };
        });
    };
  }

  clearErrorMessage() {
    this.errorMessage = null;
  }
}
